const floatKeyframes = `
  @keyframes float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-20px); }
  }
`;

export default function FflowRewards() {
  const scrollToForm = () => {
    const formElement = document.getElementById('hero-form');
    if (formElement) {
      formElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const benefits = [
    {
      icon: 'ri-coin-line',
      title: 'FFLOW Rewards App',
      description: 'Use FFLOW and save more on travel, shopping, dining and entertainment.'
    },
    {
      icon: 'ri-server-line',
      title: 'Node Ownership',
      description: 'Own a piece of the network. Node owners earn FFLOW Rewards for validation and governance of the blockchain.'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Blockchain Security',
      description: 'Your rewards and transactions are secured on the FFLOW blockchain. Transparent, immutable, and decentralized.'
    },
    {
      icon: 'ri-leaf-line',
      title: 'Conservation',
      description: 'Node owners vote for which conservation projects the FFLOW Network funds. 7% of all FFLOW mined supports wildlife and clean water projects around the world.'
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900">
      <style>{floatKeyframes}</style>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-2 bg-purple-500/20 text-purple-300 rounded-full text-sm font-semibold mb-4">
            POWERED BY BLOCKCHAIN
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Earn and Save Even More
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Introducing the FFLOW Network Blockchain. Where travel, shopping, and savings meet.
          </p>
        </div>

        {/* Coin and Content Grid */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          {/* Floating Coin */}
          <div className="relative flex justify-center">
            <div className="relative w-full max-w-[350px] aspect-square">
              {/* Glow Effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500/30 via-orange-500/20 to-transparent rounded-full blur-3xl animate-pulse"></div>

              {/* Rotating Ring */}
              <div className="absolute inset-8 border-2 border-purple-500/20 rounded-full" style={{ animation: 'spin 20s linear infinite' }}></div>

              {/* Coin */}
              <img
                src="https://static.readdy.ai/image/e51407ef51fd55054dec0ca329ae8c8f/b8d8b660209dc62de5456a36c4b24ebf.png"
                alt="FFLOW Coin"
                className="relative w-full h-full object-contain drop-shadow-2xl"
                style={{ animation: 'float 3s ease-in-out infinite' }}
              />
            </div>
          </div>

          {/* Content */}
          <div>
            <h3 className="text-3xl font-bold text-white mb-6">
              The Future of Savings and Rewards
            </h3>
            <p className="text-gray-300 mb-6 text-lg leading-relaxed">
              FFLOW is not just another coin and it is not just another rewards program. It is a full blockchain ecosystem engineered to help you keep more of your money and, if you choose to participate, unlock a level of rewards that most people never experience.
            </p>
            <p className="text-gray-300 mb-6 text-lg leading-relaxed">
              Our travel and shopping platform already gives you serious savings. Paying with your credit card can cut retail prices by 30 to 70 percent on hotels, shopping, entertainment, and more. But when you choose to pay with FFLOW, your savings go even further. You get deeper discounts, faster rewards, and a path into an ecosystem designed to reward participation.
            </p>
            <p className="text-gray-300 mb-8 text-lg leading-relaxed font-semibold">
              FFLOW turns everyday spending into a more profitable experience.
            </p>

            <button
              onClick={scrollToForm}
              className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-4 px-8 rounded-lg transition-colors cursor-pointer"
            >
              Learn More About FFLOW
            </button>
          </div>
        </div>

        {/* Payment Flow Comparison */}
        <div className="mb-16">
          <div className="text-center mb-8">
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
              How FFLOW Maximizes Your Savings
            </h3>
            <p className="text-gray-300 max-w-2xl mx-auto">
              See the difference between paying with a credit card and paying with FFLOW
            </p>
          </div>
          <div className="flex justify-center">
            <div className="bg-white rounded-2xl p-4 md:p-8 border border-purple-500/30 max-w-4xl w-full">
              <img
                src="/fflow-payment-flow.png"
                alt="FFLOW Payment Flow - Credit Card vs FFLOW comparison showing deeper savings with FFLOW"
                className="w-full h-auto rounded-lg"
              />
            </div>
          </div>
        </div>

        {/* Benefits Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {benefits.map((benefit, index) => (
            <div
              key={index}
              className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-purple-500/20 hover:border-purple-500/50 transition-colors"
            >
              <div className="w-14 h-14 bg-purple-500/20 rounded-xl flex items-center justify-center mb-4">
                <i className={`${benefit.icon} text-purple-400 text-2xl`}></i>
              </div>
              <h4 className="text-xl font-bold text-white mb-2">{benefit.title}</h4>
              <p className="text-gray-400 text-sm">{benefit.description}</p>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center">
          <div className="inline-block bg-gradient-to-r from-purple-600/20 to-orange-600/20 rounded-2xl p-8 border border-purple-500/30">
            <h3 className="text-2xl font-bold text-white mb-4">
              Ready to Join the FFLOW Revolution?
            </h3>
            <p className="text-gray-300 mb-6 max-w-xl mx-auto">
              Get started today and discover how blockchain technology is transforming travel rewards
            </p>
            <button
              onClick={scrollToForm}
              className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 px-8 rounded-lg transition-colors cursor-pointer"
            >
              Get Started Now
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
